import { useEffect, useState } from "preact/hooks"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { DeductionDetailJson, DeductionSearchResult, removeDuplicateDeduction, searchDeductions } from "@/api/deduction"
import { exactCurrencyFormatter } from "@/utils"
import { format, parseISO } from "date-fns"
import { Check, Loader2 } from "lucide-react"
import { toastResult } from "@/toast"
import { RefreshFlag } from "@/refresh_flag"

/**
 * Invoice numbers compare without punctuation, case or leading zeros — the same rule the server
 * applies (`normalize_duplicate_invoice_number`), so SV Harbor's `0018102176…` finds its ledger twin.
 */
export function normalize_duplicate_invoice_number(invoice_number: string): string {
  const alnum = invoice_number.replace(/[^a-z0-9]/gi, "").toUpperCase()
  const trimmed = alnum.replace(/^0+/, "")
  return trimmed === "" && alnum !== "" ? "0" : trimmed
}

/** The search text that finds every copy: leading zeros dropped, punctuation kept for ILIKE. */
export function duplicate_search_query(invoice_number: string): string {
  return invoice_number.trim().replace(/^0+(?=.)/, "")
}

/** The rows the server will accept as twins of `deduction`: same kind, invoice number and amount. */
export function duplicate_candidates(
  deduction: Pick<DeductionDetailJson, "id" | "kind" | "invoice_number" | "invoice_amount">,
  results: DeductionSearchResult[]
): DeductionSearchResult[] {
  const invoice = normalize_duplicate_invoice_number(deduction.invoice_number)
  const cents = Math.round(deduction.invoice_amount * 100)
  return results.filter(
    (r: DeductionSearchResult) =>
      r.id !== deduction.id &&
      r.kind === deduction.kind &&
      Math.round(r.invoice_amount * 100) === cents &&
      normalize_duplicate_invoice_number(r.invoice_number) === invoice
  )
}

interface RemoveDuplicateDialogProps {
  deduction: DeductionDetailJson
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called with the id of the copy that stayed, after this one is removed. */
  onRemoved: (kept_id: string) => void
}

export function RemoveDuplicateDialog({ deduction, open, onOpenChange, onRemoved }: RemoveDuplicateDialogProps) {
  const [candidates, setCandidates] = useState<DeductionSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [keepId, setKeepId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setKeepId(null)
    void loadCandidates()
  }, [open, deduction.id])

  async function loadCandidates() {
    setLoading(true)
    try {
      // Search strips only leading zeros: the server matches with ILIKE, so punctuation must stay.
      const results = await searchDeductions({ q: duplicate_search_query(deduction.invoice_number) })
      const twins = duplicate_candidates(deduction, results)
      setCandidates(twins)
      if (twins.length === 1) setKeepId(twins[0]!.id)
    } catch (e) {
      console.error("Failed to load duplicate candidates", e)
      setCandidates([])
    } finally {
      setLoading(false)
    }
  }

  async function handleRemove() {
    if (!keepId) return
    setSaving(true)
    try {
      const res = await removeDuplicateDeduction(deduction.id, keepId)
      toastResult(res, "Duplicate removed", "Could not remove this duplicate")
      if (res.ok) {
        onOpenChange(false)
        RefreshFlag.set(flag => !flag)
        onRemoved(res.value.data.kept_id)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Remove duplicate</DialogTitle>
          <p className="text-sm text-gray-600 mt-1">
            Pick the copy of invoice <span className="font-medium">{deduction.invoice_number}</span> (
            {exactCurrencyFormatter(deduction.invoice_amount, deduction.currency)}) to keep. This one is removed, and its
            notes, tasks, disputes, coding and files move to the copy you keep.
          </p>
        </DialogHeader>
        <div className="py-2 max-h-[300px] overflow-y-auto">
          {loading ? (
            <div className="py-6 text-center text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
              Looking for other copies...
            </div>
          ) : candidates.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-500">
              No other deduction has this invoice number and amount, so there is nothing to merge this into.
            </div>
          ) : (
            <ul className="divide-y rounded border">
              {candidates.map((item: DeductionSearchResult) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-gray-50 ${
                      keepId === item.id ? "bg-indigo-50" : ""
                    }`}
                    onClick={() => setKeepId(item.id)}>
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${keepId === item.id ? "opacity-100" : "opacity-0"}`} />
                    <div className="flex flex-col flex-1">
                      <div className="flex justify-between">
                        <span className="font-medium">Keep {item.invoice_number}</span>
                        <span className="text-sm text-gray-600">
                          {exactCurrencyFormatter(item.invoice_amount, deduction.currency)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>
                          {format(parseISO(item.invoice_date), "MMM d, yyyy")}
                          {item.customer_name && ` | ${item.customer_name}`}
                          {item.po_number && ` | PO: ${item.po_number}`}
                        </span>
                        <span>{item.category || item.kind}</span>
                      </div>
                      {item.dispute_id && <div className="mt-1 text-xs text-gray-500">Disputed</div>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleRemove} disabled={!keepId || saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Remove this copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  function result(overrides: Partial<DeductionSearchResult>): DeductionSearchResult {
    return {
      id: "twin",
      invoice_number: "18157104",
      invoice_date: "2026-04-03",
      invoice_amount: -60,
      deduction_amount: 60,
      kind: "deduction",
      category: "",
      category_description: "",
      source: "unfi",
      ...overrides,
    } as DeductionSearchResult
  }

  const sugo = { id: "self", kind: "deduction" as const, invoice_number: "18157104", invoice_amount: -60 }

  describe("duplicate_candidates", () => {
    it("finds the ledger twin of an SV Harbor line", () => {
      expect(duplicate_candidates(sugo as any, [result({})]).map(r => r.id)).toEqual(["twin"])
    })

    it("ignores this row, other amounts and other invoices that merely contain the number", () => {
      const rows = [result({ id: "self" }), result({ id: "a", invoice_amount: -63.6 }), result({ id: "b", invoice_number: "181571040" })]
      expect(duplicate_candidates(sugo as any, rows)).toEqual([])
    })

    it("searches with punctuation kept so ILIKE finds hyphenated numbers", () => {
      expect(duplicate_search_query("00INV33141-111-East")).toBe("INV33141-111-East")
      expect(duplicate_search_query("0018157104")).toBe("18157104")
      expect(duplicate_search_query("0")).toBe("0")
    })

    it("matches through leading zeros", () => {
      const row = { ...sugo, invoice_number: "0018102176120793901597", invoice_amount: -41.4 }
      const twin = result({ invoice_number: "18102176120793901597", invoice_amount: -41.4 })
      expect(duplicate_candidates(row as any, [twin])).toHaveLength(1)
    })
  })
}
